import { MarketplaceCompany } from './marketplace-schemas';

// Complete company profiles based on 2024-2025 research data
export const marketplaceCompanies: MarketplaceCompany[] = [
  {
    id: 'thumbtack',
    name: 'Thumbtack',
    founded_year: 2008,
    headquarters: 'San Francisco, CA',
    status: 'private',
    primary_segments: ['home_improvement', 'professional_services', 'handyman_services', 'cleaning_services'],
    business_model: 'lead_generation',
    description: 'Online marketplace connecting customers with local service professionals across 1,100+ services using a bidding system for projects',
    website: 'https://www.thumbtack.com',
    financial_metrics: {
      revenue: {
        amount: 300,
        year: 2024,
        reliability: 'estimated',
        source: 'Industry reports and company communications'
      },
      valuation: {
        amount: 3200,
        date: '2021-07-01',
        type: 'funding_round',
        reliability: 'verified',
        source: 'Series G funding announcement'
      },
      gmv: {
        amount: 1000,
        year: 2024,
        reliability: 'reported',
        source: 'Company processed business volume'
      }
    },
    user_metrics: {
      active_users: {
        count: 200000,
        segment: 'provider',
        period: 'annual',
        year: 2024,
        reliability: 'reported',
        source: 'Company announcements'
      }
    },
    geographic_coverage: {
      primary_markets: ['United States'],
      total_cities: 1000,
      total_states: 50,
      international: false,
      coverage_density: 'dense',
      year_updated: 2024,
      source: 'Company website and market analysis'
    },
    technology_differentiation: {
      ai_integration: {
        has_ai: true,
        applications: ['smart matching', 'price optimization', 'lead scoring'],
        competitive_advantage: true
      },
      mobile_first: true,
      instant_booking: false,
      real_time_tracking: false,
      automation_level: 'medium',
      unique_features: ['bidding system', 'instant quotes', 'background check integration'],
      last_assessed: '2024-08-22'
    },
    funding_history: {
      total_funding: {
        amount: 398,
        currency: 'USD',
        last_updated: '2021-07-01'
      },
      funding_rounds: [
        {
          round_type: 'Series G',
          amount: 275,
          date: '2021-07-01',
          lead_investors: ['Tiger Global', 'Javelin Venture Partners'],
          valuation: 3200
        }
      ]
    },
    competitive_advantages: [
      'Largest professional network with 200,000+ active pros',
      'Advanced matching algorithm',
      'Strong brand recognition',
      'Comprehensive service category coverage'
    ],
    challenges: [
      'High customer acquisition costs',
      'Competition from established players',
      'Quality control at scale',
      'Professional retention'
    ],
    last_updated: '2024-08-22',
    data_sources: ['Company reports', 'Crunchbase', 'Industry analysis', 'SEC filings']
  },
  {
    id: 'taskrabbit',
    name: 'TaskRabbit',
    founded_year: 2008,
    headquarters: 'San Francisco, CA',
    status: 'acquired',
    primary_segments: ['handyman_services', 'moving_services', 'cleaning_services', 'technology_repair'],
    business_model: 'commission_based',
    description: 'On-demand platform focusing on people who earn side income from providing services, using transaction-based business model',
    website: 'https://www.taskrabbit.com',
    financial_metrics: {
      revenue: {
        amount: 343.4,
        year: 2024,
        reliability: 'reported',
        source: 'Company financial reports'
      }
    },
    user_metrics: {
      active_users: {
        count: 60000,
        segment: 'provider',
        period: 'annual',
        year: 2024,
        reliability: 'reported',
        source: 'Company announcements'
      }
    },
    geographic_coverage: {
      primary_markets: ['United States', 'United Kingdom', 'Canada'],
      total_cities: 40,
      international: true,
      coverage_density: 'dense',
      year_updated: 2024,
      source: 'Company operations data'
    },
    technology_differentiation: {
      ai_integration: {
        has_ai: true,
        applications: ['task matching', 'price suggestions', 'demand forecasting'],
        competitive_advantage: false
      },
      mobile_first: true,
      instant_booking: true,
      real_time_tracking: true,
      automation_level: 'high',
      unique_features: ['instant booking', 'real-time tracking', 'same-day service'],
      last_assessed: '2024-08-22'
    },
    funding_history: {
      total_funding: {
        amount: 50.3,
        currency: 'USD',
        last_updated: '2017-09-01'
      },
      funding_rounds: [
        {
          round_type: 'Series D',
          amount: 37.5,
          date: '2016-04-01',
          lead_investors: ['IKEA', 'General Electric'],
          valuation: 200
        }
      ],
      acquisition_details: {
        acquirer: 'IKEA',
        date: '2017-09-01',
        status: 'completed'
      }
    },
    competitive_advantages: [
      'IKEA backing and integration',
      'Strong mobile platform',
      'International presence',
      'Real-time service tracking'
    ],
    challenges: [
      'Limited to gig workers vs professionals',
      'Smaller network than competitors',
      'Dependence on parent company strategy'
    ],
    last_updated: '2024-08-22',
    data_sources: ['IKEA reports', 'Company announcements', 'Industry analysis']
  },
  {
    id: 'angi',
    name: 'Angi (formerly HomeAdvisor)',
    founded_year: 1999,
    headquarters: 'Denver, CO',
    status: 'public',
    primary_segments: ['home_improvement', 'handyman_services', 'professional_services'],
    business_model: 'hybrid',
    description: 'Leading home services marketplace connecting homeowners with service professionals, operating multiple brands under Angi Inc.',
    website: 'https://www.angi.com',
    financial_metrics: {
      revenue: {
        amount: 267.9,
        year: 2024,
        reliability: 'verified',
        source: 'Q4 2024 earnings report'
      },
      valuation: {
        amount: 1300,
        date: '2024-08-01',
        type: 'market_cap',
        reliability: 'verified',
        source: 'Public market valuation'
      }
    },
    user_metrics: {
      active_users: {
        count: 250000,
        segment: 'provider',
        period: 'annual',
        year: 2024,
        reliability: 'reported',
        source: 'Company earnings reports'
      }
    },
    geographic_coverage: {
      primary_markets: ['United States'],
      total_states: 50,
      international: false,
      coverage_density: 'dense',
      year_updated: 2024,
      source: 'Company operations data'
    },
    technology_differentiation: {
      ai_integration: {
        has_ai: true,
        applications: ['professional matching', 'demand prediction', 'pricing optimization'],
        competitive_advantage: true
      },
      mobile_first: true,
      instant_booking: true,
      real_time_tracking: false,
      automation_level: 'medium',
      unique_features: ['multi-brand platform', 'integrated warranty', 'professional vetting'],
      last_assessed: '2024-08-22'
    },
    competitive_advantages: [
      'Largest professional network (250,000+ pros)',
      'Multiple brand portfolio',
      'Public company resources',
      'Long-established market presence'
    ],
    challenges: [
      'High customer acquisition costs',
      'Professional retention issues',
      'Platform consolidation complexity',
      'Upcoming spin-off execution'
    ],
    last_updated: '2024-08-22',
    data_sources: ['SEC filings', 'Earnings reports', 'Company announcements', 'Investor relations']
  },
  {
    id: 'handy',
    name: 'Handy',
    founded_year: 2012,
    headquarters: 'New York, NY',
    status: 'acquired',
    primary_segments: ['cleaning_services', 'handyman_services', 'moving_services'],
    business_model: 'commission_based',
    description: 'On-demand platform and gig marketplace for household services with pre-screened professionals at fixed prices',
    website: 'https://www.handy.com',
    financial_metrics: {
      revenue: {
        amount: 50,
        year: 2024,
        reliability: 'estimated',
        source: 'Post-acquisition estimates as part of Angi'
      }
    },
    user_metrics: {
      active_users: {
        count: 100000,
        segment: 'provider',
        period: 'annual',
        year: 2020,
        reliability: 'estimated',
        source: 'Pre-acquisition company data (estimated for 2020)'
      }
    },
    geographic_coverage: {
      primary_markets: ['United States', 'United Kingdom', 'Canada'],
      total_cities: 28,
      international: true,
      coverage_density: 'moderate',
      year_updated: 2024,
      source: 'Angi consolidated operations'
    },
    technology_differentiation: {
      ai_integration: {
        has_ai: false,
        applications: [],
        competitive_advantage: false
      },
      mobile_first: true,
      instant_booking: true,
      real_time_tracking: false,
      automation_level: 'low',
      unique_features: ['fixed pricing', 'pre-screened professionals', 'same-day booking'],
      last_assessed: '2024-08-22'
    },
    funding_history: {
      total_funding: {
        amount: 110.5,
        currency: 'USD',
        last_updated: '2018-10-11'
      },
      funding_rounds: [
        {
          round_type: 'Series C',
          amount: 50,
          date: '2016-06-01',
          lead_investors: ['TPG Growth', 'Fidelity Management'],
          valuation: 300
        }
      ],
      acquisition_details: {
        acquirer: 'ANGI Homeservices',
        amount: 165.5,
        date: '2018-10-11',
        status: 'completed'
      }
    },
    competitive_advantages: [
      'Fixed pricing model',
      'Strong cleaning services focus',
      'Part of larger Angi ecosystem',
      'International presence'
    ],
    challenges: [
      'Limited to basic services',
      'Integration within Angi portfolio',
      'Technology modernization needs',
      'Brand positioning clarity'
    ],
    last_updated: '2024-08-22',
    data_sources: ['Angi reports', 'Acquisition announcements', 'Historical company data']
  },
  {
    id: 'porch_group',
    name: 'Porch Group',
    founded_year: 2013,
    headquarters: 'Seattle, WA',
    status: 'public',
    primary_segments: ['home_improvement', 'professional_services'],
    business_model: 'hybrid',
    description: 'Home services platform combining marketplace with homeowners insurance and vertical software, transitioning to commission and fee-based model',
    website: 'https://porch.com',
    financial_metrics: {
      revenue: {
        amount: 100.4,
        year: 2024,
        reliability: 'verified',
        source: 'Q4 2024 earnings report'
      },
      valuation: {
        amount: 400,
        date: '2024-02-25',
        type: 'market_cap',
        reliability: 'estimated',
        source: 'Public market trading'
      }
    },
    geographic_coverage: {
      primary_markets: ['United States'],
      total_cities: 300,
      total_states: 50,
      international: false,
      coverage_density: 'moderate',
      year_updated: 2024,
      source: 'Company operations data'
    },
    technology_differentiation: {
      ai_integration: {
        has_ai: true,
        applications: ['risk assessment', 'professional matching', 'insurance underwriting'],
        competitive_advantage: true
      },
      mobile_first: false,
      instant_booking: false,
      real_time_tracking: false,
      automation_level: 'medium',
      unique_features: ['integrated insurance', 'inspection services', 'warranty products'],
      last_assessed: '2024-08-22'
    },
    competitive_advantages: [
      'Unique insurance integration',
      'Diversified revenue streams',
      'Professional software platform',
      'Data-driven risk assessment'
    ],
    challenges: [
      'Complex business model transition',
      'Smaller marketplace presence',
      'Technology integration complexity',
      'Market education needs'
    ],
    last_updated: '2024-08-22',
    data_sources: ['SEC filings', 'Earnings reports', 'Company announcements']
  }
];

// Export individual companies for easy access
export const thumbtack = marketplaceCompanies.find(c => c.id === 'thumbtack')!;
export const taskrabbit = marketplaceCompanies.find(c => c.id === 'taskrabbit')!;
export const angi = marketplaceCompanies.find(c => c.id === 'angi')!;
export const handy = marketplaceCompanies.find(c => c.id === 'handy')!;
export const porch = marketplaceCompanies.find(c => c.id === 'porch_group')!;